import { z } from "zod";

import { CURRENCIES, emailSchema, type AuditChange } from "./organizations";
import { calendarDateSchema, UNSPECIFIED_DATE } from "./people";

// What every directory party but a person has in common: the fields its record and its request carry, how its name,
// its optional text and its note are shaped, the one rule that spans two fields, what a request becomes once the API
// owns it, and how the audit log describes a change. A kind takes all of it from here — its date vocabulary included
// — so that adding one is its own fields and nothing else. A person predates this and keeps its own shapes in
// `./people.ts`.

/** The longest note a party's record carries — a page of context, not a document. */
export const PARTY_NOTE_MAX_LENGTH = 4000;

/** What someone has written about a party, as Markdown, stored exactly as the editor produced it. */
export const partyNoteSchema = z.string().max(PARTY_NOTE_MAX_LENGTH, "Use at most 4000 characters");

/** A party's name as someone types it: without surrounding spaces, at most 160 characters. */
export const partyNameSchema = z.string().trim().min(1, "A name is required").max(160, "Use at most 160 characters");

/**
 * A day in a party's record, as `YYYY-MM-DD`: this module's name for the shape People & HR gave a date, so that a
 * party kind takes its date vocabulary from the party seam and never reaches into `./people.ts`. It is a second name
 * rather than a re-export because `./index.ts` exports both modules, and `import-x/export` refuses a name two of its
 * `export *` lines carry. The word an entry uses for a date nobody gave travels with `partyDateField` below.
 */
export const partyDateSchema = calendarDateSchema;

/** A field somebody may leave empty: trimmed, capped, and sent as "" rather than left out by the form. */
export function optionalPartyText(maxLength: number): z.ZodString {
	return z.string().trim().max(maxLength, `Use at most ${maxLength} characters`);
}

/** What a vendor invoices in, or a customer is invoiced in: one of the currencies the company identity offers. */
export const partyCurrencySchema = z.enum(CURRENCIES, "Choose a currency");

/** The fields every party kind's request carries, whatever kind it is; spread them into the kind's own object. */
export const partyRequestFields = {
	name: partyNameSchema,
	/** The day the relationship started. A party whose first day has not come is "Starting". */
	since: partyDateSchema,
	/** The last day, inclusive; absent while the relationship is live. Giving one is how a party is retired. */
	endDate: partyDateSchema.optional(),
	note: partyNoteSchema.optional(),
};

/**
 * The fields every party kind's stored record carries: what its request carried, plus what the API owns. The start
 * date is the one field a record may lack where a request may not: a person always gives one, and only the Directory
 * agent adds a party without it — which leaves the party incomplete until somebody does (`missingPartyFields`).
 */
export const partyRecordFields = {
	/** The id the API gave the record when it was added, which never moves. */
	id: z.string().min(1),
	...partyRequestFields,
	since: partyDateSchema.optional(),
	/** When the record was added, as an ISO 8601 timestamp in UTC. */
	addedAt: z.iso.datetime(),
};

/**
 * Parties A→Z by name; two with the same name order by id, so every read lists them the same way. The table's store,
 * its in-memory stand-in and the frontend's fake API all order a kind's records with this, so a list is the same
 * wherever it is read. It asks for the least a record is rather than for a kind's own shape, so no kind names it
 * again.
 */
export function byPartyRecordName<Party extends { readonly id: string; readonly name: string }>(
	parties: readonly Party[],
): Party[] {
	return parties.toSorted((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

/** The name, the dates and the note of a party, as its request carries them and as its record keeps them. */
export interface PartyFields {
	readonly name: string;
	readonly since: string;
	readonly endDate?: string;
	readonly note?: string;
}

/**
 * Whom to talk to at a party and where it is: the block every party kind but a person carries, as a request sends it.
 * The form holds a field nobody filled in as an empty string rather than leaving it out, so each one accepts "" — the
 * email by way of the union, the rest because a trimmed empty string is within their length. Spread it into the kind's
 * request object.
 */
export const partyContactFields = {
	contactName: optionalPartyText(120),
	email: emailSchema.or(z.literal("")),
	phone: optionalPartyText(40),
	website: optionalPartyText(200),
	country: optionalPartyText(100),
};

/**
 * The same block as a record keeps it: a field nobody filled in is left out rather than kept as "". The email is the
 * request's own schema so that a record and the request that wrote it agree on what an address is and on the words
 * used to refuse one.
 */
export const partyContactRecordFields = {
	contactName: optionalPartyText(120).optional(),
	email: emailSchema.optional(),
	phone: optionalPartyText(40).optional(),
	website: optionalPartyText(200).optional(),
	country: optionalPartyText(100).optional(),
};

/** Whom to talk to at a party and where it is, as a request carries them: present, and empty where unfilled. */
export interface PartyContactRequest {
	readonly contactName: string;
	readonly email: string;
	readonly phone: string;
	readonly website: string;
	readonly country: string;
}

/** Whom to talk to at a party and where it is, as its record keeps them: only what somebody filled in. */
export interface PartyContact {
	readonly contactName?: string;
	readonly email?: string;
	readonly phone?: string;
	readonly website?: string;
	readonly country?: string;
}

/** An end date before the start is the one rule that spans two fields; every party kind refines on it. */
export const endDateNotBeforeSince = {
	path: ["endDate"],
	message: "The end date is before the start date",
};

/**
 * Whether a party's dates are in order: no end date at all, or one on or after the day it started. A form holds an
 * end date nobody has given as an empty string and a request leaves it out, so both count as no end date and both
 * refine on this with `endDateNotBeforeSince` beside it.
 */
export function endDateAfterSince(party: { readonly since: string; readonly endDate?: string }): boolean {
	return party.endDate === undefined || party.endDate === "" || party.endDate >= party.since;
}

/**
 * A field a person must fill in before a party is complete: its key in the record, and the label its form gives it.
 * Each kind lists its own, in form order (`VENDOR_REQUIREMENTS` and the like). A person's request always carries every
 * one of them; only the Directory agent adds a party without some, and that party is incomplete until somebody fills
 * them in. Whether a party is complete is worked out from its record every time it is asked, never stored.
 */
export type PartyRequirement<Party> = readonly [key: keyof Party & string, label: string];

/** The labels of the required fields `party` has no value for, in the order `requirements` lists them. */
export function missingPartyFields<Party>(party: Party, requirements: readonly PartyRequirement<Party>[]): string[] {
	return requirements.filter(([key]) => party[key] === undefined).map(([, label]) => label);
}

/** Field labels as a sentence names them: "Label", "Label and Start date", "Category, Currency and Start date". */
export function listOfFields(labels: readonly string[]): string {
	return new Intl.ListFormat("en-GB", { style: "long", type: "conjunction" }).format(labels);
}

/**
 * What a party's request carries into its record: its name, its dates and its note. A note of nothing but spaces is
 * no note — the editor answers an empty document that way — and a date nobody gave is left out rather than kept as
 * "" or as undefined, which the DynamoDB document client refuses.
 */
export function partyFieldsFrom(request: PartyFields): PartyFields {
	const written = request.note?.trim();
	return {
		name: request.name,
		since: request.since,
		...(request.endDate !== undefined && request.endDate !== "" && { endDate: request.endDate }),
		...(written !== undefined && written !== "" && { note: written }),
	};
}

/**
 * The optional text fields of a request somebody actually filled in, to spread into the record. One left empty is
 * left out rather than kept as "": a field nobody filled in is one the page shows as unspecified rather than as an
 * empty line, and the document client refuses an undefined attribute.
 */
export function filledPartyText<Field extends string>(
	fields: Record<Field, string | undefined>,
): Partial<Record<Field, string>> {
	const filled: Partial<Record<Field, string>> = {};
	for (const field of Object.keys(fields) as Field[]) {
		const written = fields[field]?.trim();
		if (written !== undefined && written !== "") {
			filled[field] = written;
		}
	}
	return filled;
}

/**
 * What a party's request carries into the contact block of its record: the five fields somebody filled in, each one
 * trimmed, and the rest left out. Every party kind's record is built with this, so none of them names the five again.
 */
export function partyContactFrom(request: PartyContactRequest): PartyContact {
	const { contactName, email, phone, website, country } = request;
	return filledPartyText({ contactName, email, phone, website, country });
}

/** What the audit log says instead of an optional field nobody has filled in. */
export const UNSPECIFIED_VALUE = "—";

/**
 * One field of a party's record as the audit log names it: its label, how to read it, and what an entry says when
 * nobody has filled it in — `UNSPECIFIED_VALUE`, unless the field names its own word.
 */
export type PartyField<Party> = readonly [
	label: string,
	read: (party: Party) => string | undefined,
	unspecified?: string,
];

/** A date field of a party's record: one nobody has given reads as `UNSPECIFIED_DATE`, as People & HR writes it. */
export function partyDateField<Party>(label: string, read: (party: Party) => string | undefined): PartyField<Party> {
	return [label, read, UNSPECIFIED_DATE];
}

/**
 * The contact block as the audit log names it, in the order an entry lists it. A kind spreads it into its own field
 * list wherever the block sits among that kind's fields, so every kind reads "Contact / Email / Phone / Website /
 * Country" in the same words and the same order.
 */
export const PARTY_CONTACT_AUDIT_FIELDS: readonly PartyField<PartyContact>[] = [
	["Contact", party => party.contactName],
	["Email", party => party.email],
	["Phone", party => party.phone],
	["Website", party => party.website],
	["Country", party => party.country],
];

/** What the audit log says about a note, which is what became of it and never a word of what it says. */
export function describePartyNote(before: string | undefined, after: string | undefined): AuditChange | undefined {
	if (before === after) {
		return undefined;
	}
	if (after === undefined) {
		return { label: "Note", value: "Cleared" };
	}
	return { label: "Note", value: before === undefined ? "Added" : "Changed" };
}

/**
 * Every value a party was added with, for the audit log, in the order its fields are listed and ending with its note
 * when it was added with one. A party may be written about from its first save, so that note is described here too —
 * as what became of it, never a word of what it says.
 */
export function describeNewParty<Party extends { readonly note?: string }>(
	party: Party,
	fields: readonly PartyField<Party>[],
): AuditChange[] {
	const changes: AuditChange[] = fields.map(([label, read, unspecified = UNSPECIFIED_VALUE]) => ({
		label,
		value: read(party) ?? unspecified,
	}));
	const note = describePartyNote(undefined, party.note);
	if (note !== undefined) {
		changes.push(note);
	}
	return changes;
}

/**
 * What changed between two versions of a party, newest value first, for the audit log. The note is reported as Added,
 * Changed or Cleared and is never quoted: an entry records that somebody wrote about a party, not what they wrote.
 * Mirrors `describePersonChanges` in `./people.ts`.
 */
export function describePartyChanges<Party extends { readonly note?: string }>(
	before: Party,
	after: Party,
	fields: readonly PartyField<Party>[],
): AuditChange[] {
	const changes: AuditChange[] = [];
	for (const [label, read, unspecified = UNSPECIFIED_VALUE] of fields) {
		const previous = read(before) ?? unspecified;
		const value = read(after) ?? unspecified;
		if (previous !== value) {
			changes.push({ label, value, previous });
		}
	}
	const note = describePartyNote(before.note, after.note);
	if (note !== undefined) {
		changes.push(note);
	}
	return changes;
}

/** What an audit entry of the Directory says: the action, and the detail that names the party beside it. */
export interface PartyAudit {
	readonly action: string;
	readonly detail: string;
}

/** The audit entry for a party added to the Directory: "Added party" / "Mercury · Bank". */
export function partyAddedAudit(name: string, detail: string): PartyAudit {
	return { action: "Added party", detail: `${name} · ${detail}` };
}

/** The audit entry for a party whose record changed: "Updated party" / "Mercury · Label: Insurer". */
export function partyUpdatedAudit(name: string, changes: readonly AuditChange[]): PartyAudit {
	return {
		action: "Updated party",
		detail: [name, ...changes.map(change => `${change.label}: ${change.value}`)].join(" · "),
	};
}

/** The audit entry for a party taken out of the Directory: "Removed party" / "Mercury · Bank". */
export function partyRemovedAudit(name: string, detail: string): PartyAudit {
	return { action: "Removed party", detail: `${name} · ${detail}` };
}
